import { useCallback, useEffect, useState } from "react";
import type { MarketingForm } from "./useForms";

export interface FormCanvasItem {
  portalId: string;
  formId: string;
  positionX: number;
  positionY: number;
}

interface ApiResponse {
  items: {
    portal_id?: string;
    portalId?: string;
    form_id?: string;
    formId?: string;
    position_x?: number;
    positionX?: number;
    position_y?: number;
    positionY?: number;
  }[];
  definitions: {
    formId: string;
    definition?: MarketingForm;
    error?: string;
  }[];
}

async function getJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...init });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}));
    const b = body as { error?: string; message?: string };
    throw new Error(b.error ?? b.message ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export function useFormCanvas(portalId: string | null) {
  const [items, setItems] = useState<FormCanvasItem[]>([]);
  const [definitions, setDefinitions] = useState<
    Record<string, MarketingForm | undefined>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!portalId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getJson<ApiResponse>(`/api/form-canvas/${portalId}`);
      setItems(
        data.items.map((r) => ({
          portalId: (r.portalId ?? r.portal_id) as string,
          formId: (r.formId ?? r.form_id) as string,
          positionX: (r.positionX ?? r.position_x ?? 0) as number,
          positionY: (r.positionY ?? r.position_y ?? 0) as number,
        })),
      );
      const defs: Record<string, MarketingForm | undefined> = {};
      for (const d of data.definitions) defs[d.formId] = d.definition;
      setDefinitions(defs);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [portalId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const add = useCallback(
    async (formId: string) => {
      if (!portalId) return;
      await getJson(`/api/form-canvas/${portalId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formId }),
      });
      await reload();
    },
    [portalId, reload],
  );

  const remove = useCallback(
    async (formId: string) => {
      if (!portalId) return;
      await getJson(`/api/form-canvas/${portalId}/${formId}`, {
        method: "DELETE",
      });
      await reload();
    },
    [portalId, reload],
  );

  const updatePosition = useCallback(
    async (formId: string, x: number, y: number) => {
      if (!portalId) return;
      setItems((curr) =>
        curr.map((it) =>
          it.formId === formId ? { ...it, positionX: x, positionY: y } : it,
        ),
      );
      await getJson(`/api/form-canvas/${portalId}/${formId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ x, y }),
      });
    },
    [portalId],
  );

  return { items, definitions, loading, error, add, remove, updatePosition };
}
